import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { TIMEZONES, TZ_ALIASES, CANONICAL_ZONE } from '../constants';
import { ianaOf } from '../dateUtils';
import { Icon } from './ui';

// Current UTC offset of a zone, in minutes, for sorting; 0 if unresolvable.
const offsetMinutes = (zone) => {
  const dt = DateTime.now().setZone(zone);
  return dt.isValid ? dt.offset : 0;
};

// "(UTC+05:30) Asia / Kolkata"
const zoneLabel = (zone) => {
  const dt = DateTime.now().setZone(zone);
  const off = dt.isValid ? `UTC${dt.toFormat('ZZ')}` : 'UTC';
  return `(${off}) ${zone.replace(/_/g, ' ')}`;
};

export default function TimezoneSelect({ id, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  const raw = ianaOf(value);
  const current = CANONICAL_ZONE[raw] || raw;

  // Runtime zone list, every zone that carries search aliases (so "Turkey" or
  // "Ankara" can find Europe/Istanbul before it's ever selected), plus the
  // current value if it's still not covered. Precompute label + search haystack
  // once per list, not per keystroke.
  const { options, labelOf, hayOf } = useMemo(() => {
    const set = new Set([...TIMEZONES, ...Object.keys(TZ_ALIASES)]);
    if (current) set.add(current);
    const opts = [...set].sort((a, b) => offsetMinutes(a) - offsetMinutes(b) || a.localeCompare(b));
    const labels = new Map();
    const hays = new Map();
    for (const z of opts) {
      const label = zoneLabel(z);
      labels.set(z, label);
      hays.set(z, `${z} ${z.replace(/_/g, ' ')} ${label} ${(TZ_ALIASES[z] || []).join(' ')}`.toLowerCase());
    }
    return { options: opts, labelOf: labels, hayOf: hays };
  }, [current]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!q) return options;
    const parts = q.split(' ');
    return options.filter((z) => parts.every((p) => hayOf.get(z).includes(p)));
  }, [options, hayOf, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  useEffect(() => {
    // Keep the highlighted row in view as the user arrows through.
    const el = listRef.current?.children[active];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const commit = (zone) => {
    onChange(zone);
    setOpen(false);
    setQuery('');
  };

  const openMenu = () => {
    setOpen(true);
    setQuery('');
    const i = Math.max(0, options.indexOf(current));
    setActive(i);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      openMenu();
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[active]) commit(filtered[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
  };

  return (
    <div className={`tz-select ${open ? 'open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        id={id}
        className="tz-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span className={current ? '' : 'tz-select-placeholder'}>
          {current ? (labelOf.get(current) || zoneLabel(current)) : 'Select timezone'}
        </span>
        <span aria-hidden="true" className="tz-select-caret"><Icon name="down" size={18} /></span>
      </button>

      {open && (
        <div className="tz-select-menu">
          <input
            autoFocus
            type="text"
            className="tz-select-search"
            placeholder="Search city or region…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            onKeyDown={onKeyDown}
          />
          <ul className="tz-select-list" role="listbox" ref={listRef}>
            {filtered.length === 0 && <li className="tz-select-empty">No matching timezone</li>}
            {filtered.map((z, i) => (
              <li
                key={z}
                role="option"
                aria-selected={z === current}
                className={`tz-select-option ${i === active ? 'active' : ''} ${z === current ? 'selected' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => commit(z)}
              >
                {labelOf.get(z)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
